const MONTH_NAMES = Object.freeze([
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
])

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n)
}

function canonicalMonthAbbr(mon: string): string | null {
  const idx = MONTH_NAMES.findIndex((x) => x.toLowerCase() === mon.slice(0, 3).toLowerCase())
  return idx >= 0 ? MONTH_NAMES[idx]! : null
}

function parse12hClockPart(part: string): { h: number; m: number; ap: string } | null {
  const m = part.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i)
  if (!m) return null
  const h = parseInt(m[1]!, 10)
  const mm = parseInt(m[2]!, 10)
  const ap = m[3]!.toUpperCase()
  if (h < 1 || h > 12 || mm < 0 || mm > 59) return null
  return { h, m: mm, ap }
}

/** 图二：`Dec 09, 2025, 08:00AM - 10:00AM` — 从 YYYY-MM-DD + 12h 时段解析（支持 AM 前无空格） */
function formatYmdDateWith12hRangeToDisplay(t: string): string | null {
  const range = t.match(
    /^(\d{4})-(\d{2})-(\d{2})\s+(\d{1,2}):(\d{2})\s*(AM|PM)\s*-\s*(\d{1,2}):(\d{2})\s*(AM|PM)$/i,
  )
  if (range) {
    const y = parseInt(range[1]!, 10)
    const mo = parseInt(range[2]!, 10)
    const day = parseInt(range[3]!, 10)
    const a = parse12hClockPart(`${range[4]}:${range[5]} ${range[6]}`)
    const b = parse12hClockPart(`${range[7]}:${range[8]} ${range[9]}`)
    if (!a || !b || mo < 1 || mo > 12 || day < 1 || day > 31) return null
    const mon = MONTH_NAMES[mo - 1]!
    return `${mon} ${pad2(day)}, ${y}, ${format12hClock(a.h, a.m, a.ap)} - ${format12hClock(b.h, b.m, b.ap)}`
  }
  const single = t.match(/^(\d{4})-(\d{2})-(\d{2})\s+(\d{1,2}):(\d{2})\s*(AM|PM)$/i)
  if (single) {
    const y = parseInt(single[1]!, 10)
    const mo = parseInt(single[2]!, 10)
    const day = parseInt(single[3]!, 10)
    const a = parse12hClockPart(`${single[4]}:${single[5]} ${single[6]}`)
    if (!a || mo < 1 || mo > 12 || day < 1 || day > 31) return null
    const mon = MONTH_NAMES[mo - 1]!
    return `${mon} ${pad2(day)}, ${y}, ${format12hClock(a.h, a.m, a.ap)}`
  }
  return null
}

function format12hClock(h12: number, minute: number, ap: string): string {
  return `${pad2(h12)}:${pad2(minute)}${ap.toUpperCase()}`
}

function normalizeTimeRangeRest(rest: string): string {
  const parts = rest
    .split(/\s*-\s*/)
    .map((x) => x.trim())
    .filter(Boolean)
  if (!parts.length) return rest.trim()
  return parts
    .map((p) => {
      const x = parse12hClockPart(p)
      return x ? format12hClock(x.h, x.m, x.ap) : p
    })
    .join(' - ')
}

function buildCalendarLine(monIn: string, day: number, year: number, rest: string): string {
  const mon = canonicalMonthAbbr(monIn)
  const m = mon ?? monIn.slice(0, 3)
  return `${m} ${pad2(day)}, ${year}, ${normalizeTimeRangeRest(rest)}`
}

function formatFromIsoLike(s: string): string | null {
  const d = new Date(s)
  if (Number.isNaN(d.getTime())) return null
  const mon = MONTH_NAMES[d.getMonth()]!
  const day = pad2(d.getDate())
  const y = d.getFullYear()
  const h = d.getHours()
  const mm = d.getMinutes()
  const ap = h >= 12 ? 'PM' : 'AM'
  const h12 = h % 12 === 0 ? 12 : h % 12
  return `${mon} ${day}, ${y}, ${format12hClock(h12, mm, ap)}`
}

function collapseAmPmNoSpace(s: string): string {
  return s.replace(/(\d{1,2}):(\d{2})\s+(AM|PM)/gi, (full, h, mm, ap) => {
    const x = parse12hClockPart(`${parseInt(String(h), 10)}:${parseInt(String(mm), 10)} ${String(ap)}`)
    return x ? format12hClock(x.h, x.m, x.ap) : full.replace(/\s+/g, '')
  })
}

function formatClockFromDate(d: Date): string {
  const h = d.getHours()
  const mm = d.getMinutes()
  const ap = h >= 12 ? 'PM' : 'AM'
  const h12 = h % 12 === 0 ? 12 : h % 12
  return format12hClock(h12, mm, ap)
}

function sameCalendarDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
  )
}

function parseTimeOnlyParts(raw: string): { h24: number; mm: number } | null {
  const m = raw.trim().match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/)
  if (!m?.[1] || !m[2]) return null
  const h24 = parseInt(m[1], 10)
  const mm = parseInt(m[2], 10)
  if (h24 < 0 || h24 > 23 || mm < 0 || mm > 59) return null
  return { h24, mm }
}

function format24hPartsTo12hClock(p: { h24: number; mm: number }): string {
  const ap = p.h24 >= 12 ? 'PM' : 'AM'
  const h12 = p.h24 % 12 === 0 ? 12 : p.h24 % 12
  return format12hClock(h12, p.mm, ap)
}

/**
 * 将起止时间（多为 ISO）拼成一行，再交给 {@link formatOrderScheduleLineDisplay} 归一化。
 * 同日：`Dec 09, 2025, 08:00AM - 10:00AM`
 */
export function formatServiceTimeRangeLine(
  startRaw: string,
  endRaw: string,
  serviceDateYmd?: string,
): string {
  const a = startRaw.trim()
  const b = endRaw.trim()
  const ymd = serviceDateYmd?.trim()
  if (ymd && /^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
    const p1 = parseTimeOnlyParts(a)
    const p2 = parseTimeOnlyParts(b)
    if (p1 && p2) {
      const anchor = new Date(`${ymd}T12:00:00`)
      if (!Number.isNaN(anchor.getTime())) {
        const mon = MONTH_NAMES[anchor.getMonth()]!
        const day = pad2(anchor.getDate())
        const y = anchor.getFullYear()
        return `${mon} ${day}, ${y}, ${format24hPartsTo12hClock(p1)} - ${format24hPartsTo12hClock(p2)}`
      }
    }
  }

  const d1 = new Date(a)
  const d2 = new Date(b)
  if (!Number.isNaN(d1.getTime()) && !Number.isNaN(d2.getTime())) {
    const mon = MONTH_NAMES[d1.getMonth()]!
    const day = pad2(d1.getDate())
    const y = d1.getFullYear()
    const c1 = formatClockFromDate(d1)
    const c2 = formatClockFromDate(d2)
    if (sameCalendarDay(d1, d2)) {
      return `${mon} ${day}, ${y}, ${c1} - ${c2}`
    }
    const mon2 = MONTH_NAMES[d2.getMonth()]!
    return `${mon} ${day}, ${y}, ${c1} - ${mon2} ${pad2(d2.getDate())}, ${d2.getFullYear()}, ${c2}`
  }
  return `${a} - ${b}`
}

/**
 * 订单卡片服务时间：`Dec 09, 2025, 08:00AM - 10:00AM`
 *（月份三位缩写、日期两位、时间与 AM/PM 无空格；时段用 ` - ` 分隔）
 */
export function formatOrderScheduleLineDisplay(raw: string): string {
  const t = raw.trim()
  if (!t || t === '—') return '—'
  if (/pending\s+(schedule|reschedule)/i.test(t)) return t
  if (/^pending$/i.test(t) && !/\d{4}/.test(t)) return t

  const ymd12 = formatYmdDateWith12hRangeToDisplay(t)
  if (ymd12) return ymd12

  const dot = t.match(/^([A-Za-z]{3})\s+(\d{1,2}),\s*(\d{4})\s*[·•]\s*(.+)$/i)
  if (dot) {
    const day = parseInt(dot[2]!, 10)
    const year = parseInt(dot[3]!, 10)
    return buildCalendarLine(dot[1]!, day, year, dot[4]!.trim())
  }

  const comma = t.match(/^([A-Za-z]{3})\s+(\d{1,2}),\s*(\d{4}),\s*(.+)$/i)
  if (comma) {
    const day = parseInt(comma[2]!, 10)
    const year = parseInt(comma[3]!, 10)
    return buildCalendarLine(comma[1]!, day, year, comma[4]!.trim())
  }

  if (/^\d{4}-\d{2}-\d{2}T/.test(t)) {
    const iso = formatFromIsoLike(t)
    if (iso) return iso
  }
  if (/^\d{4}-\d{2}-\d{2}/.test(t)) {
    const iso = formatFromIsoLike(t)
    if (iso) return iso
  }

  return collapseAmPmNoSpace(t)
}

function scheduleClockPartsToMinutes(h12: number, mm: number, ap: string): number {
  let h = h12 % 12
  if (ap.toUpperCase() === 'PM') h += 12
  return h * 60 + mm
}

/**
 * 从订单已排程展示文案解析时段长度（分钟），如 `03:00PM - 05:30PM` → 150。
 * 单时刻或无区间时返回 null。
 */
export function parseScheduleClockRangeMinutes(raw: string): number | null {
  const text = formatOrderScheduleLineDisplay(raw.trim())
  if (!text || text === '—' || /pending\s+(schedule|reschedule)/i.test(text)) return null
  const times = [...text.matchAll(/(\d{1,2}):(\d{2})\s*(AM|PM)/gi)]
  if (times.length < 2) return null
  const first = times[0]!
  const last = times[times.length - 1]!
  if (!first[1] || !first[2] || !last[1] || !last[2]) return null
  const a = parse12hClockPart(`${first[1]}:${first[2]} ${first[3]}`)
  const b = parse12hClockPart(`${last[1]}:${last[2]} ${last[3]}`)
  if (!a || !b) return null
  const start = scheduleClockPartsToMinutes(a.h, a.m, a.ap)
  let end = scheduleClockPartsToMinutes(b.h, b.m, b.ap)
  if (end <= start) end += 24 * 60
  const diff = end - start
  return diff > 0 ? Math.max(30, diff) : null
}

function scheduleDisplayDateFromHead(mon: string, day: number, year: number): Date | null {
  const mi = MONTH_NAMES.findIndex((x) => x.toLowerCase() === mon.slice(0, 3).toLowerCase())
  if (mi < 0) return null
  const d = new Date(year, mi, day)
  return Number.isNaN(d.getTime()) ? null : d
}

function sameScheduleCalendarDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

function lastClockRangeMatchInText(
  text: string,
): { startHm: string; startAp: string; endHm: string; endAp: string } | null {
  const rangeRe = /(\d{1,2}:\d{2})\s*(AM|PM)\s*-\s*(\d{1,2}:\d{2})\s*(AM|PM)/gi
  const ranges = [...text.matchAll(rangeRe)]
  const hit = ranges[ranges.length - 1]
  if (!hit?.[1] || !hit[2] || !hit[3] || !hit[4]) return null
  return {
    startHm: hit[1],
    startAp: hit[2].toUpperCase(),
    endHm: hit[3],
    endAp: hit[4].toUpperCase(),
  }
}

/**
 * 从可能含多段日期的展示文案中，取出 anchorDate 当日最后一段起止时刻。
 * 避免 `May 22, 08:00AM - 10:00AM, 12:00PM - 02:00PM` 被解析成 08:00–02:00。
 */
export function clockRangeFromScheduleDisplayForDate(
  raw: string,
  anchorDate: Date,
): { startHm: string; startAp: string; endHm: string; endAp: string } | null {
  const trimmed = raw.trim()
  if (!trimmed || trimmed === '—') return null

  const ymdHead = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})\s+(.+)$/i)
  if (ymdHead?.[1] && ymdHead[2] && ymdHead[3] && ymdHead[4]) {
    const y = parseInt(ymdHead[1], 10)
    const mo = parseInt(ymdHead[2], 10)
    const day = parseInt(ymdHead[3], 10)
    const segDate = new Date(y, mo - 1, day)
    if (!Number.isNaN(segDate.getTime()) && sameScheduleCalendarDay(segDate, anchorDate)) {
      return lastClockRangeMatchInText(ymdHead[4])
    }
  }

  const text = formatOrderScheduleLineDisplay(trimmed)
  if (!text || text === '—') return null

  const segmentRe =
    /([A-Za-z]{3})\s+(\d{1,2}),\s*(\d{4}),\s*(.+?)(?=(?:,\s*[A-Za-z]{3}\s+\d{1,2},\s*\d{4},)|$)/gi
  let lastOnDay: { startHm: string; startAp: string; endHm: string; endAp: string } | null = null
  for (const seg of text.matchAll(segmentRe)) {
    if (!seg[1] || !seg[2] || !seg[3] || !seg[4]) continue
    const segDate = scheduleDisplayDateFromHead(
      seg[1],
      parseInt(seg[2], 10),
      parseInt(seg[3], 10),
    )
    if (!segDate || !sameScheduleCalendarDay(segDate, anchorDate)) continue
    const hit = lastClockRangeMatchInText(seg[4])
    if (hit) lastOnDay = hit
  }
  if (lastOnDay) return lastOnDay

  const head = text.match(/^([A-Za-z]{3})\s+(\d{1,2}),\s*(\d{4}),\s*(.+)$/i)
  if (head?.[1] && head[2] && head[3] && head[4]) {
    const segDate = scheduleDisplayDateFromHead(
      head[1],
      parseInt(head[2], 10),
      parseInt(head[3], 10),
    )
    if (segDate && sameScheduleCalendarDay(segDate, anchorDate)) {
      return lastClockRangeMatchInText(head[4])
    }
  }

  return null
}

/** 订阅模板按季节划分（Schedule 标题括号内用英文月份区间） */
const SCHEDULE_SEASON_PERIODS = [
  { months: [12, 1, 2], period: 'Dec-Feb' },
  { months: [3, 4, 5], period: 'Mar-May' },
  { months: [6, 7, 8], period: 'Jun-Aug' },
  { months: [9, 10, 11], period: 'Sep-Nov' },
] as const

const SEASON_KEYWORD_TO_PERIOD: ReadonlyArray<[RegExp, string]> = [
  [/winter|冬季|冬天|\b冬\b/i, 'Dec-Feb'],
  [/spring|春季|春天|\b春\b/i, 'Mar-May'],
  [/summer|夏季|夏天|\b夏\b/i, 'Jun-Aug'],
  [/autumn|fall|秋季|秋天|\b秋\b/i, 'Sep-Nov'],
]

function scheduleSeasonPeriodForMonth(month: number): string | null {
  const hit = SCHEDULE_SEASON_PERIODS.find((s) => (s.months as readonly number[]).includes(month))
  return hit?.period ?? null
}

export interface ScheduleTemplatePeriodInput {
  label: string
  month: number | null
  start_month: number | null
  end_month: number | null
}

/** 设计稿：括号内为当季英文月份区间，如 `Dec-Feb` */
export function formatScheduleTemplatePeriodRange(template: ScheduleTemplatePeriodInput): string {
  const label = template.label.trim()

  for (const [re, period] of SEASON_KEYWORD_TO_PERIOD) {
    if (re.test(label)) return period
  }

  const parenRange = label.match(/\(([A-Za-z]{3}-[A-Za-z]{3})\s*:/)
  if (parenRange?.[1]) return parenRange[1]

  const embedded = label.match(/^([A-Za-z]{3}-[A-Za-z]{3})\s*:/)
  if (embedded?.[1]) return embedded[1]

  const stripped = label.replace(/^\(|\)$/g, '').trim()
  if (/^[A-Za-z]{3}-[A-Za-z]{3}$/.test(stripped)) return stripped

  const anchor =
    (typeof template.month === 'number' && template.month >= 1 && template.month <= 12
      ? template.month
      : null) ??
    (typeof template.start_month === 'number' &&
    template.start_month >= 1 &&
    template.start_month <= 12
      ? template.start_month
      : null)

  if (anchor != null) {
    const bySeason = scheduleSeasonPeriodForMonth(anchor)
    if (bySeason) return bySeason
  }

  const start = template.start_month
  const end = template.end_month
  if (
    typeof start === 'number' &&
    typeof end === 'number' &&
    start >= 1 &&
    start <= 12 &&
    end >= 1 &&
    end <= 12
  ) {
    if (start === 12 && end === 2) return 'Dec-Feb'
    const byStart = scheduleSeasonPeriodForMonth(start)
    const byEnd = scheduleSeasonPeriodForMonth(end)
    if (byStart && byStart === byEnd) return byStart
    if (start === end) return MONTH_NAMES[start - 1]!
    return `${MONTH_NAMES[start - 1]}-${MONTH_NAMES[end - 1]}`
  }

  const tailMonth = label.match(/(?:^|[\s·-])([A-Za-z]{3})\.?\s*$/i)
  if (tailMonth?.[1]) {
    const hit = MONTH_NAMES.find((m) => m.toLowerCase() === tailMonth[1]!.toLowerCase())
    if (hit) {
      const idx = MONTH_NAMES.indexOf(hit) + 1
      return scheduleSeasonPeriodForMonth(idx) ?? hit
    }
  }

  const beforeColon = label.split(/[:：]/)[0]?.trim().replace(/\.\s*$/, '')
  if (beforeColon && /^[A-Za-z]{3}(-[A-Za-z]{3})?$/.test(beforeColon)) return beforeColon

  return ''
}

export interface ScheduleTemplateDurationInput extends ScheduleTemplatePeriodInput {
  estimated_minutes: number
  estimated_hours?: number
  template_key?: string
  period_type?: string
  id?: number
  value?: number
}

function formatHoursDisplay(hours: number): string {
  const rounded = Math.round(hours * 10) / 10
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1)
}

/** 与模板库一致：优先 `estimated_hours`，否则用分钟换算 */
export function templateEstimatedHours(template: {
  estimated_hours?: number
  estimated_minutes: number
}): number {
  if (typeof template.estimated_hours === 'number' && template.estimated_hours > 0) {
    return template.estimated_hours
  }
  if (typeof template.estimated_minutes === 'number' && template.estimated_minutes > 0) {
    return template.estimated_minutes / 60
  }
  return 0
}

export function isInitialCareTemplate(template: ScheduleTemplateDurationInput): boolean {
  const key = (template.template_key ?? '').trim().toLowerCase()
  const periodType = (template.period_type ?? '').trim().toLowerCase()
  const label = template.label.trim().toLowerCase()
  return (
    key === 'initial' ||
    periodType === 'initial' ||
    /^initial\b/.test(label) ||
    label === 'initial'
  )
}

function templatePickId(template: ScheduleTemplateDurationInput): number | undefined {
  if (typeof template.id === 'number' && Number.isFinite(template.id)) return template.id
  if (typeof template.value === 'number' && Number.isFinite(template.value)) return template.value
  return undefined
}

/** 括号内组合名：Initial / Dec-Feb / 模板 label 前缀 */
export function templateCombinationDisplayName(template: ScheduleTemplateDurationInput): string {
  if (isInitialCareTemplate(template)) return 'Initial'
  const period = formatScheduleTemplatePeriodRange(template).replace(/\.\s*$/, '')
  if (period) return period
  const label = template.label.trim()
  const beforeColon = label.split(/[:：]/)[0]?.trim().replace(/^\(|\)$/g, '')
  if (beforeColon && !/hours?/i.test(beforeColon)) return beforeColon
  return label
}

function isFirstMonthOfScheduleSeason(month: number): boolean {
  const season = SCHEDULE_SEASON_PERIODS.find((s) => (s.months as readonly number[]).includes(month))
  return season ? season.months[0] === month : false
}

export interface ShouldIncludeInitialTemplateParams {
  isFollowUp: boolean
  baselineItemCount: number
  planKey?: string
  serviceCycleMonth?: number | null
  seasonalTemplate: ScheduleTemplateDurationInput
  planTemplates: ScheduleTemplateDurationInput[]
}

/** 设计 3.2：首访/含 baseline 时括号组合含 Initial */
export function shouldIncludeInitialTemplateForOrder(
  params: ShouldIncludeInitialTemplateParams,
): boolean {
  if (params.isFollowUp) return false
  if (!params.planTemplates.some(isInitialCareTemplate)) return false
  if (isInitialCareTemplate(params.seasonalTemplate)) return false
  if (params.baselineItemCount > 0) return true

  const planKey = (params.planKey ?? '').trim().toLowerCase()
  if (planKey === 'essential_care' || planKey === 'enhanced_care') return true
  if (planKey === 'premium_care' && params.serviceCycleMonth != null) {
    return isFirstMonthOfScheduleSeason(params.serviceCycleMonth)
  }
  return false
}

function monthInTemplateRange(month: number, start: number, end: number): boolean {
  if (start <= end) return month >= start && month <= end
  return month >= start || month <= end
}

export function findSeasonalTemplateForOrder(
  planTemplates: ScheduleTemplateDurationInput[],
  careTemplateId: number,
  serviceCycleMonth?: number | null,
): ScheduleTemplateDurationInput | undefined {
  const byId = planTemplates.find(
    (t) => t.id === careTemplateId || t.value === careTemplateId,
  )
  if (byId) return byId

  if (serviceCycleMonth == null) return undefined

  const nonInitial = planTemplates.filter((t) => !isInitialCareTemplate(t))
  const byMonth = nonInitial.find((t) => t.month === serviceCycleMonth)
  if (byMonth) return byMonth

  const byRange = nonInitial.find((t) => {
    const start = t.start_month
    const end = t.end_month
    if (
      typeof start === 'number' &&
      typeof end === 'number' &&
      start >= 1 &&
      start <= 12 &&
      end >= 1 &&
      end <= 12
    ) {
      return monthInTemplateRange(serviceCycleMonth, start, end)
    }
    return false
  })
  if (byRange) return byRange

  return nonInitial.find((t) => {
    const period = formatScheduleTemplatePeriodRange(t)
    if (!period) return false
    const season = SCHEDULE_SEASON_PERIODS.find((s) => s.period === period)
    return season ? (season.months as readonly number[]).includes(serviceCycleMonth) : false
  })
}

export interface ResolveScheduleTemplatesParams {
  planTemplates: ScheduleTemplateDurationInput[]
  careTemplateId: number
  serviceCycleMonth?: number | null
  includeInitial: boolean
}

export function resolveScheduleTemplatesForOrder(
  params: ResolveScheduleTemplatesParams,
): ScheduleTemplateDurationInput[] {
  const seasonal = findSeasonalTemplateForOrder(
    params.planTemplates,
    params.careTemplateId,
    params.serviceCycleMonth,
  )
  if (!seasonal) return []

  const initial = params.planTemplates.find(isInitialCareTemplate)
  if (!params.includeInitial || !initial) return [seasonal]

  const seasonalId = templatePickId(seasonal)
  const initialId = templatePickId(initial)
  if (seasonalId != null && initialId != null && seasonalId === initialId) return [seasonal]

  return [initial, seasonal]
}

export interface BuildScheduleDurationLabelParams {
  templates: ScheduleTemplateDurationInput[]
}

/** 设计稿：`Schedule … (Initial & Dec-Feb: 3.5 hours) for …` */
export function buildScheduleDurationLabel(params: BuildScheduleDurationLabelParams): string {
  const { templates } = params
  if (!templates.length) return ''
  if (templates.length === 1) return formatScheduleDurationLabel(templates[0]!)

  const names = templates.map(templateCombinationDisplayName).filter(Boolean)
  const combo = names.join(' & ')
  const totalHours = templates.reduce((sum, t) => sum + templateEstimatedHours(t), 0)
  if (!combo || totalHours <= 0) return formatScheduleDurationLabel(templates[0]!)
  return `(${combo}: ${formatHoursDisplay(totalHours)} hours)`
}

export interface BuildScheduleDurationLabelForOrderParams {
  planTemplates: ScheduleTemplateDurationInput[]
  careTemplateId: number
  planKey?: string
  serviceCycleMonth?: number | null
  isFollowUp?: boolean
  baselineItemCount?: number
}

export function buildScheduleDurationLabelForOrder(
  params: BuildScheduleDurationLabelForOrderParams,
): string {
  const seasonal = findSeasonalTemplateForOrder(
    params.planTemplates,
    params.careTemplateId,
    params.serviceCycleMonth,
  )
  if (!seasonal) {
    const fallback = params.planTemplates.find(
      (t) => t.id === params.careTemplateId || t.value === params.careTemplateId,
    )
    return fallback ? formatScheduleDurationLabel(fallback) : ''
  }

  const templates = resolveScheduleTemplatesForOrder({
    planTemplates: params.planTemplates,
    careTemplateId: params.careTemplateId,
    serviceCycleMonth: params.serviceCycleMonth,
    includeInitial: shouldIncludeInitialTemplateForOrder({
      isFollowUp: params.isFollowUp ?? false,
      baselineItemCount: params.baselineItemCount ?? 0,
      planKey: params.planKey,
      serviceCycleMonth: params.serviceCycleMonth,
      seasonalTemplate: seasonal,
      planTemplates: params.planTemplates,
    }),
  })
  return buildScheduleDurationLabel({ templates })
}

/** 设计稿：`Schedule Essential Care (Dec-Feb: 3.5 hours) for …` — 单模板 */
export function formatScheduleDurationLabel(
  template: ScheduleTemplateDurationInput,
): string {
  const label = template.label.trim()
  const normalized = label.match(/^\(?\s*([A-Za-z]{3}-[A-Za-z]{3}\s*:\s*[\d.]+\s*hours)\s*\)?$/i)
  if (normalized?.[1]) return `(${normalized[1].replace(/\s+/g, ' ')})`

  const cnHours = label.match(/^\(?\s*([^:：]+)[:：]\s*([\d.]+)\s*(?:小时|hours)\s*\)?$/i)
  if (cnHours) {
    const period =
      formatScheduleTemplatePeriodRange(template) || cnHours[1]!.trim().replace(/^\(|\)$/g, '')
    if (period) {
      const parsedHours = Number(cnHours[2])
      const hours =
        parsedHours > 0 ? formatHoursDisplay(parsedHours) : formatHoursDisplay(templateEstimatedHours(template))
      if (Number(hours) > 0) return `(${period}: ${hours} hours)`
    }
  }

  const period = formatScheduleTemplatePeriodRange(template).replace(/\.\s*$/, '')
  if (!period) return ''
  const hours = templateEstimatedHours(template)
  if (hours <= 0) return ''
  return `(${period}: ${formatHoursDisplay(hours)} hours)`
}
